import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { amazonUkSearchUrl } from "@/lib/affiliate";

export default function ShoppingListDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isPro = !!user?.is_pro;
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(() => localStorage.getItem("ff_store") || "tesco");
  const [storeLinks, setStoreLinks] = useState([]);
  const [fetchingLinks, setFetchingLinks] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [subItem, setSubItem] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subResult, setSubResult] = useState(null);
  const [freeTier, setFreeTier] = useState(null);

  useEffect(() => {
    if (isPro) return;
    api.get("/billing/free-tier")
      .then((r) => setFreeTier(r.data))
      .catch(() => { /* silent */ });
  }, [isPro, storeLinks.length]);
  const [newName, setNewName] = useState("");
  const [newQty, setNewQty] = useState("");
  const [adding, setAdding] = useState(false);
  const [shopMode, setShopMode] = useState(false);
  const [showShopModeOnboarding, setShowShopModeOnboarding] = useState(false);
  const [healthScoring, setHealthScoring] = useState(false);

  const runHealthScore = async () => {
    setHealthScoring(true);
    try {
      const { data } = await api.post(`/shopping-lists/${id}/health-score`);
      setList((prev) => ({ ...prev, health_score: data }));
      toast.success(`Basket grade ${data.grade} · ${data.score}/100`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't score the basket");
    } finally {
      setHealthScoring(false);
    }
  };
  const [shopIndex, setShopIndex] = useState(0);
  const [healthy, setHealthy] = useState(() => localStorage.getItem("ff_healthy") === "1");
  const [isAndroid] = useState(() => /Android/i.test(navigator.userAgent || ""));
  // First-visit hint pointing at the new 🔎 "Find online" button on each item row.
  const [showFindOnlineHint, setShowFindOnlineHint] = useState(false);

  // Google Shopping search → aggregates listings from every UK retailer
  // including specialty/ethnic/foreign-food shops (Amazon, Sous Chef, Mount
  // Mart, Holland & Barrett, etc.) in one place with prices.
  const buildShoppingUrl = (item) =>
    `https://www.google.com/search?tbm=shop&hl=en-GB&gl=uk&q=${encodeURIComponent(item + " UK")}`;


  useEffect(() => {
    // Show once per device, only after the user has a store selected so the list rows are visible.
    if (storeLinks.length > 0 && localStorage.getItem("ff_find_online_hint_seen") !== "1") {
      const t = setTimeout(() => setShowFindOnlineHint(true), 600);
      return () => clearTimeout(t);
    }
  }, [storeLinks.length]);

  const dismissFindOnlineHint = () => {
    setShowFindOnlineHint(false);
    try { localStorage.setItem("ff_find_online_hint_seen", "1"); } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [l, s] = await Promise.all([
          api.get(`/shopping-lists/${id}`),
          api.get("/stores"),
        ]);
        setList(l.data);
        setStores(s.data);
        // pre-fetch tesco links
        fetchStoreLinks("tesco");
      } catch {
        toast.error("List not found");
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggle = async (itemName, checked) => {
    setList((prev) => prev && ({
      ...prev,
      items: prev.items.map(it => it.name === itemName ? { ...it, checked } : it),
    }));
    setStoreLinks((prev) => prev.map(l => l.item === itemName ? { ...l, checked } : l));
    try {
      await api.patch(`/shopping-lists/${id}/toggle`, { item_name: itemName, checked });
    } catch {
      toast.error("Could not update");
    }
  };

  const fetchStoreLinks = async (sid, healthyOverride) => {
    setStoreId(sid);
    localStorage.setItem("ff_store", sid);
    setFetchingLinks(true);
    try {
      const h = healthyOverride === undefined ? healthy : healthyOverride;
      const { data } = await api.get(`/shopping-lists/${id}/store-links/${sid}`, { params: { healthy: h ? "true" : "false" } });
      setStoreLinks(data.links);
    } finally {
      setFetchingLinks(false);
    }
  };

  const toggleHealthy = () => {
    const next = !healthy;
    setHealthy(next);
    localStorage.setItem("ff_healthy", next ? "1" : "0");
    fetchStoreLinks(storeId, next);
    toast.success(next ? "Healthier picks on — searches now prefer wholegrain, low-fat, no added sugar variants." : "Standard search.");
  };

  // Per-item launch: user picks 📱 or 🌐 directly. No popup, no remembered preference.
  const launchBrowser = (link) => {
    window.open(link.url, "_blank", "noopener,noreferrer");
  };

  const launchApp = (link) => {
    if (isAndroid && link.app_url) {
      // Android intent:// launches the store app with a browser fallback baked in
      window.location.href = link.app_url;
      return;
    }
    // iOS Universal Links only fire when the navigation is NOT marked
    // noreferrer. Keep `noopener` (security) but drop `noreferrer` so iOS
    // can hand the URL to the supermarket app if it's installed.
    window.open(link.url, "_blank", "noopener");
  };

  const openSubstitute = async (itemName) => {
    setSubItem(itemName);
    setSubOpen(true);
    setSubResult(null);
    setSubLoading(true);
    try {
      const { data } = await api.post("/substitute", { item_name: itemName, store: storeId });
      setSubResult(data);
    } catch (e) {
      toast.error("Could not find substitutes");
      setSubOpen(false);
    } finally {
      setSubLoading(false);
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/shopping-lists/${id}/items`, {
        name: newName.trim(),
        quantity: newQty.trim() || null,
      });
      setList(data);
      setNewName("");
      setNewQty("");
      // Refresh store links to include new item
      fetchStoreLinks(storeId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add item");
    } finally {
      setAdding(false);
    }
  };

  const removeItem = async (name) => {
    if (!confirm(`Remove "${name}" from the list?`)) return;
    try {
      const { data } = await api.delete(`/shopping-lists/${id}/items`, { data: { name } });
      setList(data);
      setStoreLinks((prev) => prev.filter(l => l.item.toLowerCase() !== name.toLowerCase()));
    } catch {
      toast.error("Could not remove");
    }
  };

  // Open every un-ticked search in a new browser tab. (Phone apps can only run
  // one instance at a time, so "open all in the app" is impossible at the OS
  // level — Shopping Mode is the per-item app equivalent.)
  const openAll = () => {
    const pending = storeLinks.filter(l => !l.checked);
    if (pending.length === 0) {
      toast("Nothing left to add — every item is ticked.");
      return;
    }
    const storeName = stores.find(s => s.id === storeId)?.name;
    if (pending.length > 6 && !confirm(
      `Open ${pending.length} browser tabs in ${storeName}?\n\n` +
      `Tip: for the ${storeName} app, use Shopping Mode instead — it walks you through items one at a time.`
    )) {
      return;
    }
    pending.forEach((l) => window.open(l.url, "_blank", "noopener,noreferrer"));
    toast.success(
      `Opening ${pending.length} ${storeName} tabs. Allow pop-ups if you see fewer.`
    );
  };

  const startShopMode = () => {
    const firstPending = storeLinks.findIndex(l => !l.checked);
    setShopIndex(firstPending === -1 ? 0 : firstPending);
    // First-run onboarding: show explainer modal once per device
    if (localStorage.getItem("ff_shopmode_onboarded_v1") !== "1") {
      setShowShopModeOnboarding(true);
    } else {
      setShopMode(true);
    }
  };

  const dismissShopModeOnboarding = () => {
    localStorage.setItem("ff_shopmode_onboarded_v1", "1");
    setShowShopModeOnboarding(false);
    setShopMode(true);
  };

  const sendToPartner = async () => {
    if (!list) return;
    const storeName = stores.find(s => s.id === storeId)?.name || "supermarket";
    // Ensure we have a share token so the recipient can open the list without logging in
    let publicUrl = window.location.href;
    try {
      const { data } = await api.post(`/shopping-lists/${id}/share`);
      if (data?.share_token) {
        // Use the /api/share/link/{token} URL so messengers render a rich per-list
        // preview card (dynamic OG image + title). Real browsers get redirected to
        // the interactive React tick-off page automatically.
        publicUrl = `${window.location.origin}/api/share/link/${data.share_token}`;
      }
    } catch {
      // fall back to the authenticated URL if share creation fails
    }
    const title = `🛒 ${list.name} — Shop, Cal & Meal`;
    const introText =
      `${list.items.length} items · ready for ${storeName}.\n` +
      `Tap the link to open the live list — you can tick items off as you shop, no login needed.`;

    if (navigator.share) {
      try {
        await navigator.share({ title, text: introText, url: publicUrl });
        toast.success("Shared 📲 — your partner can tick items off as they shop");
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }

    const lines = list.items.map((it) => {
      const qty = it.quantity ? ` (${it.quantity})` : "";
      const tick = it.checked ? "✅" : "•";
      return `${tick} ${it.name}${qty}`;
    });
    const text =
      `${title}\n` +
      `${introText}\n\n` +
      `👉 ${publicUrl}\n\n` +
      `${lines.join("\n")}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp — pick a contact 📲");
  };

  const copyShareLink = async () => {
    if (!list) return;
    try {
      const { data } = await api.post(`/shopping-lists/${id}/share`);
      const publicUrl = `${window.location.origin}/api/share/link/${data.share_token}`;
      const title = `🛒 ${list.name} — Shop, Cal & Meal`;
      const shareText = `${list.items.length} items ready to grab. Tap the link to tick them off as you shop — no login needed.`;

      if (navigator.share) {
        try {
          await navigator.share({ title, text: shareText, url: publicUrl });
          toast.success("Shared 📲");
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return;
        }
      }

      try {
        await navigator.clipboard.writeText(publicUrl);
        toast.success("Share link copied — anyone with it can view & tick items", { duration: 6000 });
      } catch {
        toast.success(`Share link: ${publicUrl}`, { duration: 12000 });
      }
    } catch {
      toast.error("Couldn't create a share link.");
    }
  };

  const resetChecks = async () => {
    try {
      const { data } = await api.post(`/shopping-lists/${id}/reset`);
      setList(data);
      setStoreLinks((prev) => prev.map(l => ({ ...l, checked: false })));
      toast.success("All items unticked — ready to shop again");
    } catch {
      toast.error("Could not reset");
    }
  };

  const shopAdvance = async (markChecked) => {
    const cur = storeLinks[shopIndex];
    if (markChecked && cur) {
      await toggle(cur.item, true);
    }
    // find next un-ticked after current
    const next = storeLinks.findIndex((l, i) => i > shopIndex && !l.checked);
    if (next === -1) {
      // Wrap: anything still un-ticked anywhere?
      const any = storeLinks.findIndex(l => !l.checked && (!markChecked || l.item !== cur?.item));
      if (any === -1) {
        toast.success("Basket complete 🎉");
        setShopMode(false);
        return;
      }
      setShopIndex(any);
    } else {
      setShopIndex(next);
    }
  };

  if (loading) return <div className="text-[#5C6B61]">Loading…</div>;
  if (!list) return null;

  return (
    <div className="space-y-8 ff-fade-in overflow-x-hidden">
      <Link to="/app/shopping" className="text-sm text-[#5C6B61] hover:text-[#3B5A3E] inline-flex items-center gap-1">
        ← All lists
      </Link>
      <header>
        <div className="ff-label">Shopping list</div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          {list.name}
        </h1>
        <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
          <p className="text-[#5C6B61]">{list.items.length} ingredients · scaled to your servings.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={copyShareLink}
              data-testid="copy-share-link-btn"
              className="text-sm font-medium rounded-full px-4 py-2 bg-white border border-[#3B5A3E] text-[#3B5A3E] hover:bg-[#F3F0EA] transition flex items-center gap-2"
              title="Copy a public link so family or friends at the shops can open this list"
            >
              <span>🔗</span> Copy share link
            </button>
            <button
              onClick={sendToPartner}
              data-testid="whatsapp-share-btn"
              className="text-sm font-medium rounded-full px-4 py-2 bg-[#25D366] text-white hover:bg-[#1FAD55] transition flex items-center gap-2"
              title="Send this basket to a partner on WhatsApp"
            >
              <span>📲</span> Send to partner
            </button>
          </div>
        </div>
      </header>

      <HealthScoreCard
        list={list}
        isPro={isPro}
        busy={healthScoring}
        onRun={runHealthScore}
        onListChange={setList}
      />

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6 lg:gap-8">
        {/* Ingredients */}
        <section className="ff-card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="ff-label">Ingredients</div>
            <div className="text-[11px] text-[#8B9690]">
              Tap <span className="px-1.5 py-0.5 rounded-md bg-[#F3F0EA] text-[#3B5A3E] font-medium">📱</span> to add to <span className="font-medium text-[#2A332C]">{stores.find(s => s.id === storeId)?.name || "store"}</span>
            </div>
          </div>

          {/* Manual add */}
          <form onSubmit={addManual} className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              data-testid="manual-item-name"
              className="ff-input flex-1"
              placeholder="Add an extra — e.g. oat milk"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={80}
            />
            <input
              data-testid="manual-item-qty"
              className="ff-input sm:max-w-[140px]"
              placeholder="qty"
              value={newQty}
              onChange={(e) => setNewQty(e.target.value)}
            />
            <button
              type="submit"
              data-testid="manual-add-btn"
              disabled={adding || !newName.trim()}
              className="ff-btn-primary disabled:opacity-60 whitespace-nowrap"
            >
              {adding ? "Adding…" : "Add"}
            </button>
          </form>

          <ul className="space-y-1">
            {list.items.map((it, i) => {
              const link = storeLinks.find(l => l.item.toLowerCase() === it.name.toLowerCase());
              return (
              <li key={i} data-testid="ingredient-row" className="flex items-start gap-3 py-3 border-b border-[#E5E2DA] last:border-0">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#3B5A3E] flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2 flex-wrap text-[#2A332C]">
                    <span className="break-words">{it.name}</span>
                    {it.manual && (
                      <span className="ff-chip text-[10px] py-0.5 px-2" style={{ background: "#FBEFEA", color: "#A65541" }}>added</span>
                    )}
                  </div>
                  <div className="text-xs text-[#8B9690] mt-0.5 break-words">
                    {[it.quantity, it.recipes.join(", ")].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {link && (
                  <button
                    onClick={() => launchApp(link)}
                    data-testid="quick-add-app-btn"
                    title={`Add to ${stores.find(s => s.id === storeId)?.name} app`}
                    aria-label={`Add ${it.name} to store app`}
                    className="w-8 h-8 rounded-full bg-[#F3F0EA] hover:bg-[#3B5A3E] hover:text-white flex items-center justify-center flex-none transition text-sm"
                  >
                    📱
                  </button>
                )}
                <button
                  onClick={() => removeItem(it.name)}
                  className="text-xs text-[#8B9690] hover:text-[#C36952] flex-none w-6 h-8 flex items-center justify-center"
                  data-testid="remove-item-btn"
                  title="Remove from list"
                >✕</button>
              </li>
              );
            })}
          </ul>
        </section>

        {/* Send to store */}
        <section className="ff-card p-4 sm:p-6">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="ff-label">Send to a store</div>
            <button
              onClick={toggleHealthy}
              data-testid="healthy-toggle"
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition border whitespace-nowrap ${
                healthy
                  ? "bg-[#3B5A3E] text-white border-[#3B5A3E]"
                  : "bg-white text-[#5C6B61] border-[#E5E2DA] hover:border-[#3B5A3E]"
              }`}
              title="Prefer wholegrain, low-fat, no added sugar variants"
            >
              🥑 Healthier {healthy ? "ON" : "OFF"}
            </button>
          </div>
          <p className="text-sm text-[#5C6B61] mb-4">
            Choose a UK supermarket. We&apos;ll open each ingredient in their search — and you can swap anything we can&apos;t find.
          </p>
          {!isPro && freeTier?.asda_lists && (
            <FreeAsdaBanner used={freeTier.asda_lists.used} limit={freeTier.asda_lists.limit} />
          )}
          <div className="flex flex-wrap gap-2" data-testid="store-selector">
            {stores.map((s) => {
              const isLocked = !isPro && s.id !== "asda";              return (
                <button
                  key={s.id}
                  onClick={() => fetchStoreLinks(s.id)}
                  data-testid={`store-${s.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition border flex items-center gap-1.5 ${
                    storeId === s.id
                      ? "bg-[#3B5A3E] text-white border-[#3B5A3E]"
                      : isLocked
                        ? "bg-white text-[#8B9690] border-[#E5E2DA] hover:border-[#C36952]"
                        : "bg-white text-[#2A332C] border-[#E5E2DA] hover:border-[#3B5A3E]"
                  }`}
                >
                  {isLocked && <span aria-hidden>🔒</span>}
                  {s.name}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            <button
              onClick={() => fetchStoreLinks(storeId)}
              disabled={fetchingLinks}
              data-testid="send-to-store-btn"
              className="ff-btn-primary disabled:opacity-60"
            >
              {fetchingLinks ? "Building…" : "Refresh links"}
            </button>
            {storeLinks.length > 0 && (
              <>
                <button
                  onClick={openAll}
                  data-testid="open-all-btn"
                  className="ff-btn-ghost border border-[#E5E2DA] whitespace-nowrap"
                  title="Opens every un-ticked item in a new browser tab"
                >
                  🌐 Open all <span className="hidden sm:inline">in browser </span>({storeLinks.filter(l => !l.checked).length})
                </button>
                <button
                  onClick={startShopMode}
                  data-testid="shop-mode-btn"
                  className="px-5 py-2.5 rounded-full font-medium text-white bg-[#C36952] hover:bg-[#A65541] transition"
                  title="Walks you through items one at a time — works with the store app"
                >
                  📱 Shop with app
                </button>
                {storeLinks.some(l => l.checked) && (
                  <button
                    onClick={resetChecks}
                    data-testid="reset-checks-btn"
                    className="ff-btn-ghost border border-[#E5E2DA] text-[#A65541]"
                    title="Uncheck every item so you can shop the list again"
                  >
                    ↻ Untick all
                  </button>
                )}
              </>
            )}
          </div>

          {storeLinks.length > 0 && (
            <div className="mt-6 space-y-2 max-h-[460px] overflow-y-auto pr-1">
              {storeLinks.map((l, i) => (
                <div
                  key={i}
                  data-testid="store-link-row"
                  className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 rounded-xl bg-[#F3F0EA] hover:bg-[#E5E2DA]/80 transition relative"
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium break-words ${l.checked ? "line-through text-[#8B9690]" : ""}`}>{l.item}</div>
                    <div className="text-xs text-[#8B9690] break-words">
                      {l.quantity ? `${l.quantity} · ` : ""}search: {l.query}
                      {healthy && l.query !== l.base_query && (
                        <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px]" style={{ background: "#3B5A3E", color: "#fff" }}>healthier</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    <button
                      onClick={() => openSubstitute(l.item)}
                      title="Find a swap"
                      data-testid="find-swap-btn"
                      className="text-xs font-medium text-[#A65541] hover:text-white hover:bg-[#A65541] px-2.5 py-1.5 rounded-full transition flex-none"
                    >
                      ⇄ Swap
                    </button>
                    <button
                      onClick={() => launchApp(l)}
                      title={`Open in ${stores.find(s => s.id === storeId)?.name} app`}
                      data-testid="open-app-btn"
                      className="w-9 h-9 rounded-full bg-white hover:bg-[#3B5A3E] hover:text-white text-[#3B5A3E] flex items-center justify-center flex-none transition text-base"
                      aria-label="Open in store app"
                    >
                      📱
                    </button>
                    <button
                      onClick={() => launchBrowser(l)}
                      title="Open in browser"
                      data-testid="open-browser-btn"
                      className="w-9 h-9 rounded-full bg-white hover:bg-[#3B5A3E] hover:text-white text-[#3B5A3E] flex items-center justify-center flex-none transition text-base"
                      aria-label="Open in browser"
                    >
                      🌐
                    </button>
                    <a
                      href={buildShoppingUrl(l.item)}
                      target="_blank"
                      rel="noreferrer"
                      title="Find this anywhere online — Google Shopping searches every UK retailer"
                      aria-label="Find this anywhere online"
                      data-testid="find-online-btn"
                      onClick={() => {
                        // Fire-and-forget — feeds the owner Stock Requests board
                        api.post("/stock-requests/log", {
                          item_name: l.item,
                          source: "google_fallback",
                          store_id: storeId,
                          list_id: list?.list_id,
                        }).catch(() => { /* best-effort */ });
                        dismissFindOnlineHint();
                      }}
                      className="w-9 h-9 rounded-full bg-white hover:bg-[#3B5A3E] hover:text-white text-[#3B5A3E] flex items-center justify-center flex-none transition text-base relative"
                    >
                      🔎
                      {i === 0 && showFindOnlineHint && (
                        <FindOnlineHint onDismiss={dismissFindOnlineHint} />
                      )}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Substitution modal */}
      {subOpen && (
        <div
          className="fixed inset-0 z-40 bg-[#2A332C]/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setSubOpen(false)}
          data-testid="substitution-modal"
        >
          <div
            className="ff-card w-full max-w-lg p-6 md:p-8 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ff-label">Smart swap</div>
            <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              Can&apos;t find <span className="text-[#A65541]">{subItem}</span>?
            </h2>
            <p className="text-sm text-[#5C6B61] mt-1">Here are alternatives that work at {stores.find(s => s.id === storeId)?.name || "this store"}.</p>

            {subLoading ? (
              <div className="mt-8 text-[#5C6B61] text-sm">Asking the chef…</div>
            ) : subResult ? (
              <div className="mt-5 space-y-3">
                {subResult.alternatives.map((a, i) => (
                  <a
                    key={i}
                    href={a.url || `https://www.google.com/search?q=${encodeURIComponent(a.search_query)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="swap-alternative"
                    className="block rounded-2xl border border-[#E5E2DA] hover:border-[#3B5A3E] p-4 transition"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{a.name}</div>
                      <span className="text-[#3B5A3E] text-sm">Open ↗</span>
                    </div>
                    <p className="text-sm text-[#5C6B61] mt-1">{a.reason}</p>
                  </a>
                ))}
                {subResult.tip && (
                  <div className="text-xs text-[#5C6B61] mt-3 bg-[#F3F0EA] rounded-xl p-3">
                    💡 {subResult.tip}
                  </div>
                )}
              </div>
            ) : null}

            {/* Last-resort fallback: search Google for offline / specialty sources */}
            {subItem && !subLoading && (
              <div className="mt-5 pt-5 border-t border-[#E5E2DA]">
                <div className="ff-label">Still can&apos;t find it?</div>
                <p className="text-xs text-[#5C6B61] mt-1 mb-3">
                  Search every UK shop via Google Shopping — covers specialty grocers, ethnic / foreign food retailers, butchers and online-only delivery.
                </p>
                <a
                  href={buildShoppingUrl(subItem)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="search-google-fallback"
                  onClick={() => {
                    // Fire-and-forget: log this as a "stock request" so the owner sees
                    // which ingredients customers can't find. Never blocks the click.
                    api.post("/stock-requests/log", {
                      item_name: subItem,
                      source: "google_fallback",
                      store_id: storeId,
                      list_id: list?.list_id,
                    }).catch(() => { /* best-effort */ });
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-white border border-[#E5E2DA] text-[#2A332C] hover:border-[#3B5A3E] hover:text-[#3B5A3E] transition"
                >
                  🔎 Search every UK shop
                </a>
                <a
                  href={amazonUkSearchUrl(subItem)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="search-amazon-fallback"
                  className="inline-flex items-center gap-2 px-4 py-2 ml-2 rounded-full text-xs font-semibold bg-white border border-[#E5E2DA] text-[#2A332C] hover:border-[#3B5A3E] hover:text-[#3B5A3E] transition"
                >
                  🛒 Amazon UK
                </a>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button onClick={() => setSubOpen(false)} className="ff-btn-ghost" data-testid="close-swap-btn">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Shop Mode — full-screen guided checkout */}
      {showShopModeOnboarding && (
        <ShopModeOnboarding
          storeName={stores.find(s => s.id === storeId)?.name || "your supermarket"}
          onContinue={dismissShopModeOnboarding}
          onCancel={() => setShowShopModeOnboarding(false)}
        />
      )}
      {shopMode && storeLinks[shopIndex] && (
        <ShopMode
          storeName={stores.find(s => s.id === storeId)?.name || "Store"}
          items={storeLinks}
          index={shopIndex}
          setIndex={setShopIndex}
          onOpenApp={() => launchApp(storeLinks[shopIndex])}
          onOpenBrowser={() => launchBrowser(storeLinks[shopIndex])}
          onAdded={() => shopAdvance(true)}
          onSkip={() => shopAdvance(false)}
          onClose={() => setShopMode(false)}
          onFindOnline={(itemName) => {
            // Log the missing item to the owner Stock Requests board
            api.post("/stock-requests/log", {
              item_name: itemName,
              source: "google_fallback",
              store_id: storeId,
              list_id: list?.list_id,
            }).catch(() => { /* best-effort */ });
          }}
        />
      )}
    </div>
  );
}

function ShopMode({ storeName, items, index, setIndex, onOpenApp, onOpenBrowser, onAdded, onSkip, onClose, onFindOnline }) {
  const total = items.length;
  const ticked = items.filter(i => i.checked).length;
  const cur = items[index];
  const progressPct = total === 0 ? 0 : Math.round((ticked / total) * 100);

  return createPortal((
    <div
      data-testid="shop-mode-overlay"
      className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col"
    >
      {/* Header */}
      <header className="border-b border-[#E5E2DA] px-6 py-4 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="ff-label">{storeName} · Shopping mode</div>
          <div className="text-sm text-[#5C6B61] mt-1">
            <span className="font-medium text-[#2A332C]" data-testid="shop-mode-progress">{ticked}/{total}</span> in your basket
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="shop-mode-close-btn"
          className="text-[#5C6B61] hover:text-[#2A332C] text-2xl leading-none"
        >×</button>
      </header>

      {/* Progress bar */}
      <div className="h-1.5 bg-[#F3F0EA]">
        <div className="h-full bg-[#3B5A3E] transition-all" style={{ width: `${progressPct}%` }} />
      </div>

      {/* How-this-works strip — always visible so nobody expects auto-add */}
      <div
        data-testid="shop-mode-howto-strip"
        className="border-b border-[#E5E2DA] bg-[#FFF6E3] px-4 py-3"
      >
        <div className="max-w-3xl mx-auto flex items-start gap-3">
          <span className="text-xl flex-none leading-none mt-0.5" aria-hidden>💡</span>
          <div className="flex-1 min-w-0 text-xs sm:text-sm text-[#5A4A1F] leading-relaxed">
            <strong className="text-[#2A332C]">How this works:</strong>{" "}
            <span className="inline-flex items-center gap-1">
              <span className="inline-flex w-4 h-4 rounded-full bg-[#3B5A3E] text-white text-[10px] font-bold items-center justify-center">1</span>
              <span>Tap <strong>Open in {storeName}</strong></span>
            </span>{" "}·{" "}
            <span className="inline-flex items-center gap-1">
              <span className="inline-flex w-4 h-4 rounded-full bg-[#3B5A3E] text-white text-[10px] font-bold items-center justify-center">2</span>
              <span>Tap the green <strong>Add</strong> button in {storeName}</span>
            </span>{" "}·{" "}
            <span className="inline-flex items-center gap-1">
              <span className="inline-flex w-4 h-4 rounded-full bg-[#3B5A3E] text-white text-[10px] font-bold items-center justify-center">3</span>
              <span>Come back &amp; tap <strong>I added it ✓</strong> for the next item</span>
            </span>
          </div>
        </div>
      </div>

      {/* Item carousel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12 text-center">
        <div className="ff-label mb-3">Item {index + 1} of {total}</div>
        {cur.checked && (
          <div className="ff-chip mb-4" style={{ background: "#E8F0E9", color: "#3B5A3E" }}>
            Already ticked — you can skip
          </div>
        )}
        <h2
          data-testid="shop-mode-item-name"
          className={`text-4xl sm:text-5xl font-semibold tracking-tight max-w-2xl ${cur.checked ? "line-through text-[#8B9690]" : ""}`}
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {cur.item}
        </h2>
        {cur.quantity && (
          <p className="text-lg text-[#5C6B61] mt-3">{cur.quantity}</p>
        )}
        <p className="text-sm text-[#8B9690] mt-2">searching <span className="font-mono text-[#2A332C]">{cur.query}</span></p>

        <div className="flex flex-col gap-3 mt-10 w-full max-w-md">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onOpenApp}
              data-testid="shop-mode-open-app-btn"
              className="ff-btn-primary text-base py-3.5 flex items-center justify-center gap-2"
            >
              <span>📱</span> Open in {storeName}
            </button>
            <button
              onClick={onOpenBrowser}
              data-testid="shop-mode-open-browser-btn"
              className="text-base py-3.5 rounded-full font-medium bg-white border border-[#3B5A3E] text-[#3B5A3E] hover:bg-[#F3F0EA] transition flex items-center justify-center gap-2"
            >
              <span>🌐</span> Open in browser
            </button>
          </div>
          <div className="text-xs text-[#8B9690] text-center -mb-1">
            👆 Tap above to search · 👇 then come back &amp; tick it off
          </div>
          <button
            onClick={onAdded}
            data-testid="shop-mode-added-btn"
            className="text-base py-3.5 rounded-full font-medium bg-[#C36952] text-white hover:bg-[#A65541] transition"
          >
            ✓ I added it — next item →
          </button>
          <a
            href={`https://www.google.com/search?tbm=shop&hl=en-GB&gl=uk&q=${encodeURIComponent(cur.item + " UK")}`}
            target="_blank"
            rel="noreferrer"
            data-testid="shop-mode-find-online-btn"
            onClick={() => onFindOnline?.(cur.item)}
            className="text-sm py-2.5 rounded-full font-medium bg-white border border-[#E5E2DA] text-[#5C6B61] hover:border-[#3B5A3E] hover:text-[#3B5A3E] transition flex items-center justify-center gap-2"
          >
            <span>🔎</span> Search every UK shop
          </a>
        </div>
        <button
          onClick={onSkip}
          data-testid="shop-mode-skip-btn"
          className="mt-4 text-sm text-[#5C6B61] hover:text-[#2A332C] underline-offset-4 hover:underline"
        >
          Skip for now →
        </button>
      </div>

      {/* Bottom strip with item dots */}
      <div className="border-t border-[#E5E2DA] px-6 py-4">
        <div className="flex gap-1.5 overflow-x-auto justify-center">
          {items.map((it, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              title={it.item}
              className={`w-2.5 h-2.5 rounded-full transition flex-none ${
                i === index ? "bg-[#3B5A3E] scale-150" : it.checked ? "bg-[#C36952]" : "bg-[#E5E2DA]"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  ), document.body);
}

function ShopModeOnboarding({ storeName, onContinue, onCancel }) {
  return createPortal((
    <div
      data-testid="shop-mode-onboarding"
      className="fixed inset-0 z-[60] bg-[#2A332C]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onCancel}
    >
      <div
        className="ff-card max-w-md w-full p-6 md:p-8 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-xl flex-none">📱</span>
          <div className="flex-1 min-w-0">
            <div className="ff-label text-[#3B5A3E]">Before you start</div>
            <h3
              className="text-xl md:text-2xl font-semibold mt-1 leading-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Heads up — you&apos;ll tap &quot;Add&quot; yourself
            </h3>
          </div>
        </div>

        <p className="text-sm text-[#5C6B61] mt-4 leading-relaxed">
          Shop, Cal &amp; Meal can&apos;t put items in your <strong>{storeName}</strong> basket directly — only you can.
          What we <em>do</em> is open every item for you, one tap at a time, so you don&apos;t have to type or search.
        </p>

        <ol className="mt-5 space-y-3" data-testid="shop-mode-onboarding-steps">
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 rounded-full bg-[#3B5A3E] text-white text-sm font-bold flex items-center justify-center flex-none">1</span>
            <span className="text-sm text-[#2A332C] leading-relaxed mt-0.5">
              Tap <strong>Open in {storeName}</strong> — your supermarket loads with the item pre-searched.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 rounded-full bg-[#3B5A3E] text-white text-sm font-bold flex items-center justify-center flex-none">2</span>
            <span className="text-sm text-[#2A332C] leading-relaxed mt-0.5">
              Tap the green <strong>Add</strong> button on the product you want, inside {storeName}.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 rounded-full bg-[#C36952] text-white text-sm font-bold flex items-center justify-center flex-none">3</span>
            <span className="text-sm text-[#2A332C] leading-relaxed mt-0.5">
              Switch back here and tap <strong>I added it ✓</strong> — we&apos;ll jump to the next item.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 rounded-full bg-[#3B5A3E] text-white text-sm font-bold flex items-center justify-center flex-none">✓</span>
            <span className="text-sm text-[#2A332C] leading-relaxed mt-0.5">
              When all items are ticked, head to <strong>{storeName}</strong> and check out as normal.
            </span>
          </li>
        </ol>

        <div className="mt-6 flex gap-2">
          <button
            onClick={onCancel}
            data-testid="shop-mode-onboarding-cancel"
            className="ff-btn-ghost flex-none px-5"
          >
            Not now
          </button>
          <button
            onClick={onContinue}
            data-testid="shop-mode-onboarding-continue"
            className="ff-btn-primary flex-1"
          >
            Got it — let&apos;s shop →
          </button>
        </div>
        <p className="text-[11px] text-[#8B9690] text-center mt-3">
          You&apos;ll only see this the first time.
        </p>
      </div>
    </div>
  ), document.body);
}

const GRADE_STYLES = {
  A: { bg: "#D8EAD9", fg: "#1F4023", label: "Excellent" },
  B: { bg: "#E8F0E9", fg: "#3B5A3E", label: "Good" },
  C: { bg: "#FFF4E5", fg: "#8A5C12", label: "Okay" },
  D: { bg: "#FBE5DE", fg: "#A33D26", label: "Could improve" },
  E: { bg: "#F8D3CA", fg: "#7B1F0F", label: "High room for improvement" },
};

function HealthScoreCard({ list, isPro, busy, onRun, onListChange }) {
  const score = list?.health_score;

  if (!isPro) {
    return (
      <div className="ff-card p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="health-score-paywall">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="text-2xl flex-none">🥗</span>
          <div className="flex-1 min-w-0">
            <div className="ff-label">Healthier basket score</div>
            <p className="text-sm text-[#5C6B61] mt-1 leading-relaxed">
              Get a 0-100 nutrition score, friendly verdict and concrete UK swaps — a Shop, Cal & Meal Pro feature.
            </p>
          </div>
        </div>
        <Link
          to="/app/billing"
          className="ff-btn-primary text-sm whitespace-nowrap self-stretch sm:self-auto text-center"
          data-testid="health-score-upgrade"
        >
          Unlock with Pro
        </Link>
      </div>
    );
  }

  if (!score) {
    return (
      <div className="ff-card p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="health-score-empty">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="text-2xl flex-none">🥗</span>
          <div className="flex-1 min-w-0">
            <div className="ff-label">Healthier basket score</div>
            <p className="text-sm text-[#5C6B61] mt-1 leading-relaxed">Rate this basket out of 100 — get healthier swaps in seconds.</p>
          </div>
        </div>
        <button
          onClick={onRun}
          disabled={busy}
          data-testid="health-score-run"
          className="ff-btn-primary text-sm disabled:opacity-60 whitespace-nowrap self-stretch sm:self-auto"
        >
          {busy ? "Scoring…" : "Rate my basket"}
        </button>
      </div>
    );
  }

  const grade = (score.grade || "C").toUpperCase();
  const style = GRADE_STYLES[grade] || GRADE_STYLES.C;
  return (
    <div className="ff-card p-6" data-testid="health-score-result">
      <div className="flex items-start gap-5 flex-wrap">
        <div
          className="w-24 h-24 rounded-2xl flex flex-col items-center justify-center flex-none shadow-inner"
          style={{ background: style.bg, color: style.fg }}
          data-testid="health-score-grade"
        >
          <div className="text-4xl font-semibold leading-none" style={{ fontFamily: "Outfit, sans-serif" }}>{grade}</div>
          <div className="text-xs mt-1 opacity-80">{score.score}/100</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="ff-label">Healthier basket score · {style.label}</div>
          <p className="text-base text-[#2A332C] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            {score.headline}
          </p>
          {(score.highlights || []).length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-2" data-testid="health-score-highlights">
              {score.highlights.map((h, i) => (
                <li key={i} className="text-[11px] bg-[#E8F0E9] text-[#3B5A3E] font-medium px-2.5 py-1 rounded-full">
                  ✓ {h}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          onClick={onRun}
          disabled={busy}
          data-testid="health-score-rerun"
          className="text-xs text-[#5C6B61] hover:text-[#3B5A3E] disabled:opacity-60"
        >
          {busy ? "…" : "↻ Re-score"}
        </button>
      </div>

      {(score.swaps || []).length > 0 && (
        <div className="mt-5">
          <div className="ff-label">Healthier swaps</div>
          <ul className="mt-2 space-y-2" data-testid="health-score-swaps">
            {score.swaps.map((s, i) => (
              <SwapRow key={i} swap={s} index={i} listId={list?.list_id} onListChange={onListChange} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SwapRow({ swap, index, listId, onListChange }) {
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(!!swap.applied);
  const [error, setError] = useState(null);

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/shopping-lists/${listId}/apply-swap`, {
        from_name: swap.from,
        to_name: swap.to,
      });
      setApplied(true);
      if (data.list && onListChange) onListChange(data.list);
      toast.success(`Swapped ${data.from} → ${data.to}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't apply swap");
      toast.error(e?.response?.data?.detail || "Couldn't apply swap");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className={`flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 text-sm rounded-xl p-3 transition ${applied ? "bg-[#E8F0E9]" : "bg-[#FAF9F6]"}`}
      data-testid={`health-score-swap-${index}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 leading-snug">
          <span className="text-[#A65541] font-semibold break-words">{swap.from}</span>
          <span className="text-[#8B9690]">→</span>
          <span className="text-[#3B5A3E] font-semibold break-words">{swap.to}</span>
        </div>
        {swap.why && <div className="text-xs text-[#5C6B61] mt-1 leading-relaxed">{swap.why}</div>}
        {error && <div className="text-[11px] text-[#A33D26] mt-1">{error}</div>}
      </div>
      <button
        onClick={apply}
        disabled={busy || applied}
        data-testid={`apply-swap-${index}`}
        className={`text-xs font-medium rounded-full px-4 py-2 whitespace-nowrap transition disabled:opacity-60 self-stretch sm:self-start ${
          applied
            ? "bg-white text-[#3B5A3E] border border-[#cfe0d0]"
            : "bg-[#3B5A3E] text-white hover:bg-[#2c4530]"
        }`}
      >
        {applied ? "✓ Applied" : busy ? "Swapping…" : "Apply swap"}
      </button>
    </li>
  );
}



function FindOnlineHint({ onDismiss }) {
  return (
    <div
      data-testid="find-online-hint"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss(); }}
      className="absolute z-30 right-0 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 top-full mt-2 w-64 ff-card p-3 text-left shadow-xl border-2 border-[#3B5A3E] cursor-pointer animate-in fade-in slide-in-from-top-2 duration-300"
      role="tooltip"
    >
      <div className="flex items-start gap-2">
        <div className="text-lg flex-none">✨</div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-[#3B5A3E]">Smart finds</div>
          <p className="text-xs text-[#2A332C] mt-1 leading-snug">
            Can&apos;t find an item at the supermarket? Tap the magnifier to search <strong>every UK shop</strong> via Google Shopping &mdash; great for specialty, ethnic or foreign foods.
          </p>
          <button
            data-testid="find-online-hint-dismiss"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss(); }}
            className="mt-2 text-[10px] font-semibold tracking-wider uppercase text-[#3B5A3E] hover:text-[#2c4530]"
          >
            Got it
          </button>
        </div>
      </div>
      {/* Pointer arrow */}
      <div className="absolute -top-1.5 right-3 sm:right-1/2 sm:translate-x-1/2 w-3 h-3 rotate-45 bg-white border-l-2 border-t-2 border-[#3B5A3E]"></div>
    </div>
  );
}

function FreeAsdaBanner({ used, limit }) {
  const remaining = Math.max(0, (limit || 0) - (used || 0));
  const isMaxed = remaining === 0;
  const triggerUpsell = () => {
    try {
      window.dispatchEvent(new CustomEvent("pro-upsell", { detail: {
        code: isMaxed ? "pro_limit_asda_lists" : "pro_supermarket_preview",
        message: isMaxed
          ? `You've used your ${limit} free shopping lists. Start your 24-hour free trial to unlock unlimited.`
          : "Unlock every UK supermarket and unlimited shopping lists with Pro.",
        feature: "Unlimited shopping lists",
      } }));
    } catch { /* ignore */ }
  };
  return (
    <div
      onClick={triggerUpsell}
      role="button"
      tabIndex={0}
      data-testid="free-asda-banner"
      className={`mb-3 rounded-2xl px-4 py-3 text-sm cursor-pointer flex items-center gap-3 transition border ${
        isMaxed
          ? "bg-[#FCF0EC] border-[#E8C4B8] text-[#A33D26]"
          : "bg-[#FAF6EE] border-[#E0D5C2] text-[#5C6B61] hover:border-[#3B5A3E]"
      }`}
    >
      <span className="text-lg flex-none">{isMaxed ? "🔒" : "🛒"}</span>
      <span className="flex-1 min-w-0">
        {isMaxed ? (
          <>You&apos;ve used your <strong>{used} / {limit}</strong> free Asda shopping lists. <span className="underline">Start your 24-hour free trial</span> to unlock unlimited lists + every supermarket.</>
        ) : (
          <><strong>{used} / {limit}</strong> free Asda shopping lists used. <span className="underline">Upgrade to Pro</span> for unlimited lists + every supermarket.</>
        )}
      </span>
    </div>
  );
}

